/**
 * Profile.tsx - 个人中心页面
 *
 * 功能概述：
 * - 展示当前登录用户的基本信息（用户名/姓名/角色/身份/频道/邮箱）
 * - 修改登录密码：校验原密码 → 新密码（≥6位）→ 确认一致
 * - 修改成功后自动退出登录，跳转登录页用新密码重新登录
 *
 * 权限：所有已登录角色（admin / operator / viewer）均可访问
 */

import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Form, Input, Button, Tag, Typography, message, Space, Divider } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getProfile, changePassword } from '../api/auth';
import { useAuthStore } from '../store/authStore';
import { handleApiError } from '../utils/errorHandler';

const { Title, Text } = Typography;

interface ProfileInfo {
  id: number;
  username: string;
  realName?: string;
  email?: string;
  role: string;
  identity: string;
  channelName?: string;
}

const ROLE_TAG: Record<string, { color: string; label: string }> = {
  admin: { color: 'gold', label: '管理员' },
  operator: { color: 'blue', label: '运维' },
  viewer: { color: 'default', label: '只读' },
};

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [profile, setProfile] = useState<ProfileInfo | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    getProfile()
      .then((res) => setProfile(res.data as ProfileInfo))
      .catch((err) => handleApiError(err, '获取个人信息失败'));
  }, []);

  /** 提交修改密码：成功后退出登录并跳转登录页 */
  const handleChangePassword = async (values: { oldPassword: string; newPassword: string }) => {
    try {
      setSubmitting(true);
      await changePassword(values.oldPassword, values.newPassword);
      message.success('密码修改成功，请使用新密码重新登录');
      // 稍作停留让用户看到提示，再退出登录
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 1200);
    } catch (error: any) {
      // 后端 400 的 message 为具体原因（原密码错误等），优先展示
      const reason = error?.response?.data?.message;
      if (typeof reason === 'string') {
        message.error(reason);
      } else {
        handleApiError(error, '密码修改失败');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const roleTag = ROLE_TAG[profile?.role || ''] || { color: 'default', label: profile?.role || '-' };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <Title level={2}>
        <UserOutlined style={{ marginRight: 8 }} />
        个人中心
      </Title>

      <Card title="基本信息" size="small">
        <Descriptions column={2} size="small">
          <Descriptions.Item label="用户名">{profile?.username || '-'}</Descriptions.Item>
          <Descriptions.Item label="姓名">{profile?.realName || '-'}</Descriptions.Item>
          <Descriptions.Item label="角色">
            <Tag color={roleTag.color}>{roleTag.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="身份">
            <Tag color={profile?.identity === 'admin' ? 'purple' : 'cyan'}>
              {profile?.identity === 'admin' ? '系统管理员' : '普通用户'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="所属频道">{profile?.channelName || 'client'}</Descriptions.Item>
          <Descriptions.Item label="邮箱">{profile?.email || '-'}</Descriptions.Item>
        </Descriptions>
        <Divider style={{ margin: '12px 0' }} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          角色、频道等信息由管理员在「管理员管理」中维护；如需修改请联系管理员。
        </Text>
      </Card>

      <Card
        title={
          <Space>
            <SafetyOutlined />
            <span>修改密码</span>
          </Space>
        }
        size="small"
        style={{ marginTop: 16 }}
      >
        <Form form={form} layout="vertical" onFinish={handleChangePassword} style={{ maxWidth: 400 }}>
          <Form.Item
            name="oldPassword"
            label="原密码"
            rules={[{ required: true, message: '请输入原密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入原密码" autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '新密码至少6个字符' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="至少6个字符" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的新密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="再次输入新密码" autoComplete="new-password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              确认修改
            </Button>
            <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
              修改成功后需重新登录
            </Text>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Profile;
